import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { restaurantAPI, paymentAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function PlanSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [error, setError] = useState('');
  const [monthlyFee, setMonthlyFee] = useState(999);
  const [startWithTrial, setStartWithTrial] = useState(false);
  const [restaurant, setRestaurant] = useState(null);

  // Check if this is a renewal (from URL parameter)
  const isRenewal = searchParams.get('renewal') === 'true';

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoadingStatus(true);
      const response = await restaurantAPI.getSubscriptionPlanStatus();
      const data = response.data.data;

      // Check if restaurant is approved first
      if (!data.isApproved || data.approvalStatus !== 'approved') {
        navigate('/restaurant/onboarding');
        return;
      }

      // Check if restaurant has active monthly subscription
      if (data.subscriptionPlan?.type === 'monthly') {
        const status = data.subscriptionPlan.monthly?.status;
        if (status === 'active' || status === 'trial') {
          // Already has active subscription, redirect to dashboard
          navigate('/restaurant/dashboard');
          return;
        }
      }

      setRestaurant(data);
      // Get city-based monthly fee
      setMonthlyFee(data.cityMonthlyFee || 999);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setError('Failed to load subscription information');
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleStartSubscription = async () => {
    setLoading(true);
    setError('');

    try {
      // For renewals, never allow trial (safety check)
      const canStartTrial = startWithTrial && !isRenewal;

      if (canStartTrial) {
        // Start with free trial (only for first-time subscriptions)
        const result = await restaurantAPI.selectSubscriptionPlan({
          planType: 'monthly',
          startTrial: true
        });

        if (result.data.success) {
          await refreshUser();
          alert('Free trial activated! Welcome to Foodwinc!');
          navigate('/restaurant/dashboard');
        }
      } else {
        // Initiate monthly payment
        await initiateMonthlyPayment();
      }
    } catch (error) {
      console.error('Error starting subscription:', error);
      setError(error.response?.data?.message || 'Failed to start subscription');
      setLoading(false);
    }
  };

  const initiateMonthlyPayment = async () => {
    try {
      // Create payment session
      const sessionResult = await paymentAPI.createSubscriptionPayment({
        amount: monthlyFee
      });

      if (!sessionResult.data.success) {
        setError(sessionResult.data.message || 'Failed to create payment session');
        setLoading(false);
        return;
      }

      const { paymentSessionId, cashfreeOrderId } = sessionResult.data.data;

      // Redirect to Cashfree payment
      if (window.Cashfree) {
        // Use sandbox for TEST credentials, production for PROD
        const cashfreeMode = process.env.REACT_APP_CASHFREE_ENV === 'PROD' ? 'production' : 'sandbox';
        const cashfree = window.Cashfree({ mode: cashfreeMode });
        cashfree.checkout({
          paymentSessionId,
          redirectTarget: '_self',
          returnUrl: `${window.location.origin}/restaurant/payment-verification?order_id=${cashfreeOrderId}`
        });
      } else {
        // Fallback: redirect to payment verification with order ID
        window.location.href = `${window.location.origin}/restaurant/payment-verification?order_id=${cashfreeOrderId}`;
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      setError(error.response?.data?.message || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  if (loadingStatus) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F8FAFC'
      }}>
        <div style={{
          fontSize: '16px',
          color: '#64748B',
          fontWeight: '500'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F8FAFC',
      padding: '24px 16px'
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px 24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: isRenewal ? '#FF6961' : '#10B981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '32px'
            }}>
              {isRenewal ? '🔄' : '🎉'}
            </div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#0F172A',
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>
              {isRenewal ? 'Renew Your Subscription' : 'Congratulations!'}
            </h1>
            <p style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#0F172A',
              marginBottom: '4px',
              margin: '0 0 4px 0'
            }}>
              {isRenewal ? 'Your subscription has expired' : 'Your Restaurant is Approved'}
            </p>
            <p style={{
              color: '#64748B',
              fontSize: '15px',
              margin: '0',
              lineHeight: '1.5'
            }}>
              {isRenewal
                ? 'Renew now to continue receiving orders on Foodwinc'
                : 'Subscribe to start receiving orders on Foodwinc'}
            </p>
          </div>

          {error && (
            <div style={{
              padding: '16px',
              background: '#FEE2E2',
              color: '#DC2626',
              borderRadius: '12px',
              marginBottom: '24px',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #FCA5A5'
            }}>
              {error}
            </div>
          )}

          {/* Monthly Subscription Card */}
          <div style={{
            padding: '24px',
            border: '2px solid #FF6961',
            borderRadius: '16px',
            background: 'rgba(255, 105, 97, 0.04)',
            position: 'relative',
            boxShadow: '0 4px 16px rgba(255, 105, 97, 0.08)',
            marginBottom: '24px'
          }}>
            {/* Badge */}
            <div style={{
              position: 'absolute',
              top: '-12px',
              right: '20px',
              background: '#FF6961',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: '700',
              boxShadow: '0 2px 8px rgba(255, 105, 97, 0.25)',
              letterSpacing: '0.5px'
            }}>
              RECOMMENDED
            </div>

            <div style={{ textAlign: 'center', marginBottom: '16px' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>📅</div>
              <h2 style={{
                color: '#FF6961',
                marginBottom: '12px',
                fontSize: '22px',
                fontWeight: '700',
                margin: '0 0 12px 0'
              }}>
                Monthly Subscription
              </h2>
              <div style={{ marginBottom: '12px' }}>
                <span style={{
                  fontSize: '40px',
                  fontWeight: '700',
                  color: '#FF6961'
                }}>
                  ₹{monthlyFee}
                </span>
                <span style={{
                  fontSize: '16px',
                  color: '#64748B',
                  marginLeft: '8px',
                  fontWeight: '500'
                }}>
                  /month
                </span>
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '20px',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <span style={{ fontSize: '16px' }}>🎉</span>
                <span style={{
                  color: '#10B981',
                  fontWeight: '700',
                  fontSize: '14px'
                }}>
                  ZERO COMMISSION on all orders!
                </span>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div style={{
            background: '#F8FAFC',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid #E2E8F0',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '18px',
              marginBottom: '16px',
              color: '#0F172A',
              fontWeight: '700',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>✨</span>
              What You Get
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { icon: '✓', text: '0% commission on every order', color: '#FF6961' },
                { icon: '✓', text: 'Keep 100% of your food revenue', color: '#FF6961' },
                { icon: '✓', text: 'Unlimited orders per month', color: '#FF6961' },
                { icon: '✓', text: 'Access to all platform features', color: '#FF6961' },
                { icon: '✓', text: 'Priority customer support', color: '#FF6961' },
                { icon: '✓', text: 'Cancel anytime', color: '#FF6961' }
              ].map((benefit, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '8px 0'
                }}>
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: benefit.color,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>
                    {benefit.icon}
                  </div>
                  <span style={{
                    flex: 1,
                    fontWeight: '500',
                    color: '#0F172A',
                    fontSize: '15px'
                  }}>
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Options */}
          <div style={{
            background: 'rgba(255, 105, 97, 0.04)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 105, 97, 0.2)',
            marginBottom: '24px'
          }}>
            <h3 style={{
              fontSize: '16px',
              marginBottom: '16px',
              color: '#0F172A',
              fontWeight: '700',
              margin: '0 0 16px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>💳</span>
              {isRenewal ? 'Renew Subscription' : 'Choose How to Start'}
            </h3>

            {/* For renewals, only show Pay Now option */}
            {isRenewal ? (
              <div style={{
                padding: '16px',
                background: 'white',
                borderRadius: '12px',
                border: '2px solid #FF6961',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>💳</span>
                <div>
                  <div style={{
                    fontWeight: '700',
                    fontSize: '16px',
                    color: '#0F172A',
                    marginBottom: '4px'
                  }}>
                    Pay ₹{monthlyFee}
                  </div>
                  <div style={{
                    color: '#64748B',
                    fontSize: '14px'
                  }}>
                    Renew for another 30 days of unlimited orders.
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Option 1: Free Trial (only for first-time) */}
                <div
                  onClick={() => setStartWithTrial(true)}
                  style={{
                    padding: '16px',
                    background: startWithTrial ? 'white' : '#F8FAFC',
                    borderRadius: '12px',
                    border: `2px solid ${startWithTrial ? '#10B981' : '#E2E8F0'}`,
                    cursor: 'pointer',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="radio"
                    checked={startWithTrial}
                    onChange={() => setStartWithTrial(true)}
                    style={{
                      width: '20px',
                      height: '20px',
                      accentColor: '#10B981',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        fontWeight: '700',
                        fontSize: '16px',
                        color: '#0F172A'
                      }}>
                        Start with 1 Month Free Trial
                      </span>
                      <span style={{
                        background: '#10B981',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '700',
                        letterSpacing: '0.5px'
                      }}>
                        FREE
                      </span>
                    </div>
                    <div style={{
                      color: '#64748B',
                      fontSize: '14px'
                    }}>
                      Try Foodwinc free for 30 days. No payment now.
                    </div>
                  </div>
                </div>

                {/* Option 2: Pay Now */}
                <div
                  onClick={() => setStartWithTrial(false)}
                  style={{
                    padding: '16px',
                    background: !startWithTrial ? 'white' : '#F8FAFC',
                    borderRadius: '12px',
                    border: `2px solid ${!startWithTrial ? '#FF6961' : '#E2E8F0'}`,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="radio"
                    checked={!startWithTrial}
                    onChange={() => setStartWithTrial(false)}
                    style={{
                      width: '20px',
                      height: '20px',
                      accentColor: '#FF6961',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: '700',
                      fontSize: '16px',
                      color: '#0F172A',
                      marginBottom: '4px'
                    }}>
                      Pay ₹{monthlyFee} Now
                    </div>
                    <div style={{
                      color: '#64748B',
                      fontSize: '14px'
                    }}>
                      Start immediately with full access for 30 days.
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Submit Button */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleStartSubscription}
              disabled={loading}
              style={{
                width: '100%',
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '700',
                background: isRenewal
                  ? '#FF6961'
                  : (startWithTrial ? '#10B981' : '#FF6961'),
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'all 0.2s ease',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(255, 105, 97, 0.25)',
                letterSpacing: '0.3px'
              }}
            >
              {loading ? 'Processing...' : (
                isRenewal
                  ? `Pay ₹${monthlyFee} & Renew`
                  : (startWithTrial ? 'Start Free Trial' : `Pay ₹${monthlyFee} & Subscribe`)
              )}
            </button>
          </div>

          {/* Logout Button */}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <button
              onClick={handleLogout}
              style={{
                background: 'none',
                border: 'none',
                color: '#DC2626',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                padding: '8px 16px',
                borderRadius: '8px',
                transition: 'background 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.background = '#FEE2E2'}
              onMouseLeave={(e) => e.target.style.background = 'none'}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PlanSelection;
